import React from 'react';

const fmt = (value) =>
  `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

function Card({ label, value, subtitle, subtitleColor }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-1 min-w-0">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-2xl font-bold text-gray-900">{fmt(value)}</span>
      {subtitle && (
        <span className={`text-xs font-medium ${subtitleColor || 'text-gray-400'}`}>
          {subtitle}
        </span>
      )}
    </div>
  );
}

export default function DashboardSummary({ summary, loading }) {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-gray-200 p-5 h-[100px] animate-pulse"
          >
            <div className="h-3 w-24 bg-gray-100 rounded mb-3" />
            <div className="h-6 w-32 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const {
    revenue,
    prevRevenue,
    bills,
    receivable,
    payable,
    invoicesDueCount,
    billsDueCount,
    monthLabel,
  } = summary;

  let revenueSubtitle = '';
  let revenueSubColor = 'text-gray-400';
  if (prevRevenue > 0) {
    const pctChange = ((revenue - prevRevenue) / prevRevenue) * 100;
    const arrow = pctChange >= 0 ? '↑' : '↓';
    revenueSubtitle = `${arrow} ${Math.abs(pctChange).toFixed(0)}% vs last month`;
    revenueSubColor = pctChange >= 0 ? 'text-emerald-600' : 'text-red-500';
  } else if (revenue > 0) {
    revenueSubtitle = 'No revenue last month';
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <Card
        label={`Revenue (${monthLabel})`}
        value={revenue}
        subtitle={revenueSubtitle}
        subtitleColor={revenueSubColor}
      />
      <Card
        label="Receivable"
        value={receivable}
        subtitle={
          invoicesDueCount > 0
            ? `${invoicesDueCount} invoice${invoicesDueCount !== 1 ? 's' : ''} due`
            : 'All paid'
        }
        subtitleColor={invoicesDueCount > 0 ? 'text-amber-600' : 'text-emerald-600'}
      />
      <Card
        label={`Bill (${monthLabel})`}
        value={bills}
      />
      <Card
        label="Payable"
        value={payable}
        subtitle={
          billsDueCount > 0
            ? `${billsDueCount} bill${billsDueCount !== 1 ? 's' : ''} due`
            : 'All paid'
        }
        subtitleColor={billsDueCount > 0 ? 'text-red-500' : 'text-emerald-600'}
      />
    </div>
  );
}
